import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { DEVELOPMENT_DOMAINS } from '@saesak/shared';
import { AppBar, Screen } from '@/components/layout';
import { Button, Card, Chip, EmptyState, ErrorNote, Spinner } from '@/components/ui';
import { apiData } from '@/lib/api';
import { formatDate } from '@/lib/format';
import { useCanWrite } from '@/store/auth';
import type { ObservationSummary } from '@/types';

/**
 * 관찰메모 — 교사 전용, 발달기록의 근거 자료 (§5.14).
 * 기존 "우리 반 기록"의 관찰 탭을 독립 메뉴로 분리했다(알림장 중복 정리).
 * 새 메모 작성은 화면의 빠른 작성 버튼(FAB)으로 한다.
 */
export function ObservationsPage() {
  const canWrite = useCanWrite();
  const { data, isPending, isError, refetch } = useQuery({
    queryKey: ['observations'],
    queryFn: () => apiData<ObservationSummary[]>('/observations'),
  });

  const domainName = (code: string) =>
    DEVELOPMENT_DOMAINS.find((d) => d.code === code)?.name ?? code;

  return (
    <Screen>
      <AppBar title="관찰메모" back />

      {isPending && <Spinner />}
      {isError && (
        <ErrorNote message="관찰기록을 불러오지 못했어요." onRetry={() => void refetch()} />
      )}

      {!isPending && !isError && (data?.length ?? 0) === 0 && (
        <EmptyState
          title="아직 관찰메모가 없어요"
          body="아이의 순간을 짧게 남겨 두면 발달기록을 쓸 때 근거가 돼요."
          action={
            canWrite ? (
              <Link to="/records/observations/new">
                <Button>첫 관찰 남기기</Button>
              </Link>
            ) : undefined
          }
        />
      )}

      <ul className="flex flex-col gap-3 p-4">
        {data?.map((observation) => (
          <Card key={observation.id} as="li">
            <div className="mb-1.5 flex flex-wrap items-center gap-1.5">
              {observation.children.map((child) => (
                <Chip key={child.id} tone="primary">
                  {child.name}
                </Chip>
              ))}
              <span className="ml-auto text-sm text-ink-sub">{formatDate(observation.observedOn)}</span>
            </div>
            <p className="whitespace-pre-wrap text-base">{observation.content}</p>
            {observation.domainCodes.length > 0 && (
              <p className="mt-1.5 text-sm text-ink-sub">
                {observation.domainCodes.map(domainName).join(' · ')}
              </p>
            )}
          </Card>
        ))}
      </ul>
    </Screen>
  );
}
